import React, {useEffect, useState} from "react";
import Form from "../../lib/appfuse-react/component/Form";
import Box from "../../lib/appfuse-react/component/Box";
import Typography from "@material-ui/core/Typography";
import i18n from "../../lib/appfuse-react/core/i18n";
import {Checkbox, FormControlLabel, Grid, Icon, IconButton, TextField, Tooltip} from "@material-ui/core";
import SelectField from "../../lib/appfuse-react/component/SelectField";
import Button from "../../lib/appfuse-react/component/Button";
import UserEvent from "../../lib/appfuse-react/core/UserEvent";
import lang from "../../lib/appfuse-react/core/lang";
import downloader from "../../lib/appfuse-react/core/downloader";
import DataTable, {DataColumn} from "../../lib/appfuse-react/component/DataTable";
import {DialogActions, DialogContent, DialogTitle, StyledDialog} from "../../component/Dialog";
import FileField from "../../lib/appfuse-react/component/FileField";
import usePrompt from "../../lib/appfuse-react/hook/usePrompt";
import {useSelector} from "../../store";
import {
    selectRegistrationForm
} from "../../reducer/iam/registrationReducer";
import Stepper from "@material-ui/core/Stepper";
import Step from "@material-ui/core/Step";
import StepLabel from "@material-ui/core/StepLabel";
import NumberField from "../../lib/appfuse-react/component/NumberField";
import registrationService from "../../service/iam/registrationService";
import {makeStyles} from "@material-ui/core/styles";

const STEPS = {
    initial: ['Information About the Applicant', 'Transportation Information', 'Laboratory Information'],
    onlyCrop: ['Information About the Applicant', 'Transportation Information', 'Laboratory Information', 'Farm Information'],
    onlyProcessing: ['Information About the Applicant', 'Transportation Information', 'Laboratory Information', 'Processing Unit Information'],
    both: ['Information About the Applicant', 'Transportation Information', 'Laboratory Information',
           'Processing Unit Information', 'Farm Information'],
};

const FORMS = {
    initial: [ApplicantInfo, TransportationInfo, LabInfo],
    onlyCrop: [ApplicantInfo, TransportationInfo, LabInfo, FarmInfo],
    onlyProcessing: [ApplicantInfo, TransportationInfo, LabInfo, FactoryInfo],
    both: [ApplicantInfo, TransportationInfo, LabInfo, FactoryInfo, FarmInfo]
};

const useStyles = makeStyles(theme => ({
    downloadText: {
        cursor: "pointer",
        '&:hover': {
            color: theme.palette.secondary.main
        }
    }
}))

/**
 * 境外註冊
 * @param props
 * @constructor
 */

function OverseasRegistration(props){
    const { value, activeStep } = props;
    const { applyCrop=false, applyProcessing=false } = value;
    const steps = applyCrop && applyProcessing? STEPS.both : (applyCrop && !applyProcessing? STEPS.onlyCrop : (!applyCrop && applyProcessing? STEPS.onlyProcessing : STEPS.initial));
    const forms = applyCrop && applyProcessing? FORMS.both : (applyCrop && !applyProcessing? FORMS.onlyCrop : (!applyCrop && applyProcessing? FORMS.onlyProcessing : FORMS.initial));
    const title = steps[activeStep-1];
    const OverseasForms = forms[activeStep-1] || forms[0];
    const prompt = usePrompt();

    let dictionary = {};
    if (OverseasForms === LabInfo) {
        dictionary = {name: 'Laboratory name'};
    } else if (OverseasForms === FactoryInfo) {
        dictionary = {name: 'Unit name', product: 'Product name', latitude: 'Unit Latitude' , longitude: 'Unit Longitude', address: 'Unit address'};
    } else {
        dictionary = {name: 'Farm/Unit name', acreage: 'Acreage(ha)', address: 'Farm address'};
    }

    const handleNext = async (data={}) => {
        try {
            await registrationService.overseasValidate(data);
            props.onNext();
        } catch (e) {
            prompt.alert(e,{title: "Invalid input.", dictionary});
        }
    }

    const handleComplete = async (validateData, data) => {
        try {
            await registrationService.overseasValidate(validateData);

            await registrationService.overseasPost(data);

            props.onComplete();
        } catch (e) {
            prompt.alert(e, {title: "Registration failed.", dictionary});
        }
    }

    return <OverseasForms value={value} onChange={props.onChange} title={title}
                          onNext={handleNext} onBack={props.onBack} onComplete={handleComplete} />
}

// 申請者資訊
function ApplicantInfo(props){
    const { value, title } = props;

    const validateData = {
        applicant: value?.applicant || null,
        contactPerson: value?.contactPerson || null,
        email: value?.email || null,
        contactPhone: value?.contactPhone || null,
        idNo: value?.idNo || null,
        representative: value?.representative || null,
        certificationStandards: value?.certificationStandards || null,
        legalStatus: value?.legalStatus || null,
        legalStatusDesc: value?.legalStatusDesc || null,
        businessTypes: value?.businessTypes || null,
        mailingAddress: value?.mailingAddress || null,
        applyCrop: value?.applyCrop || false,
        cropCertificates: value?.cropCertificates || null,
        applyProcessing: value?.applyProcessing || false,
        processingCertificates: value?.processingCertificates || null
    }

    return (
        <Form value={value} onChange={props.onChange} fullWidth fullHeight display="flex" flexDirection="column" overflow="hidden">
            <Box display="flex" paddingTop={1} paddingBottom={1} fullWidth>
                <Typography variant="h5">{i18n.translate(title)}</Typography>
            </Box>
            <Box fullWidth fullHeight paddingTop={1} overflow="auto">
                <Grid container spacing={2} xs={12}>
                    {/*申請人*/}
                    <Grid item xs={3}>
                        <TextField name="applicant" label="Applicant name" variant="outlined"
                                   fullWidth required/>
                    </Grid>
                    {/*聯絡人*/}
                    <Grid item xs={3}>
                        <TextField name="contactPerson" label="Contact person" variant="outlined"
                                   fullWidth required/>
                    </Grid>
                    {/*電子郵件*/}
                    <Grid item xs={3}>
                        <TextField name="email" label="Email" variant="outlined"
                                   fullWidth required/>
                    </Grid>
                    {/*聯絡電話*/}
                    <Grid item xs={3}>
                        <TextField name="contactPhone" label="Contact phone" variant="outlined"
                                   fullWidth required/>
                    </Grid>
                    {/*統編/商業登記編號*/}
                    <Grid item xs={3}>
                        <TextField name="idNo" label="Tax id number/Business registered number" variant="outlined"
                                   fullWidth required/>
                    </Grid>
                    {/*負責人*/}
                    <Grid item xs={3}>
                        <TextField name="representative" label="Representative" variant="outlined"
                                   fullWidth />
                    </Grid>
                    {/*驗證標準*/}
                    <Grid item xs={6}>
                        <SelectField name="certificationStandards" label="Certification standard" variant="outlined" multiple fullWidth
                                     required code="certificationStandard" />
                    </Grid>
                    {/*法律身份*/}
                    <Grid item xs={3}>
                        <SelectField name="legalStatus" label="Legal status" variant="outlined" fullWidth code="processingLegalStatus"/>
                    </Grid>
                    {/*其他(請說明)*/}
                    <Grid item xs={3}>
                        <TextField name="legalStatusDesc" label="Others(specify)" variant="outlined"
                                   placeholder={i18n.translate("Please specify")} fullWidth/>
                    </Grid>
                    {/*申請者業務型態*/}
                    <Grid item xs={6}>
                        <SelectField name="businessTypes" label="Applicant operation type" variant="outlined" fullWidth multiple
                                     required code="processingBusinessType"/>
                    </Grid>
                    {/*通訊地址*/}
                    <Grid item xs={12}>
                        <TextField name="mailingAddress" label="Mailing address" variant="outlined" fullWidth/>
                    </Grid>
                    <Grid item xs={12}>
                        <Box display="flex" gap={2} alignItems="center">
                            <Typography color="textSecondary">{i18n.translate("Certification Scope Applied")}</Typography>
                            <Typography variant="subtitle2" color="textPrimary">{i18n.translate("Please select at least one type")}</Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12}>
                        <Grid container spacing={1}>
                            {/*申請作物類別*/}
                            <Grid item xs={3}>
                                <FormControlLabel
                                    control={<Checkbox/>}
                                    name="applyCrop"
                                    label="Crop"
                                />
                            </Grid>
                            {/*作物其他有機驗證標準證書資訊*/}
                            { value?.applyCrop &&
                                <Grid item xs={9}>
                                    <Certificates name="cropCertificates" type="array"/>
                                </Grid>
                            }
                        </Grid>
                    </Grid>
                    <Grid item xs={12}>
                        <Grid container spacing={1}>
                            {/*申請加工/分裝流通*/}
                            <Grid item xs={3}>
                                <FormControlLabel
                                    control={<Checkbox/>}
                                    name="applyProcessing"
                                    label="Processing"
                                />
                            </Grid>
                            {/*加分流其他有機驗證標準證書資訊*/}
                            { value?.applyProcessing &&
                                <Grid item xs={9}>
                                    <Certificates name="processingCertificates" type="array"/>
                                </Grid>
                            }
                        </Grid>
                    </Grid>
                </Grid>
            </Box>
            <Box display="flex" justifyContent="space-between" paddingTop={2}>
                <Button text="Back" color="secondary" onClick={props.onBack} />
                <Button text="Next" variant="contained" color="primary" onClick={() => props.onNext(validateData)} />
            </Box>
        </Form>
    )
}

// 交通資訊
function TransportationInfo(props){
    const { value, title } = props;

    return (
        <Form value={value} onChange={props.onChange} fullWidth fullHeight display="flex" flexDirection="column" overflow="hidden">
            <Box display="flex" paddingTop={1} paddingBottom={1} fullWidth>
                <Typography variant="h5">{i18n.translate(title)}</Typography>
            </Box>
            <Box fullWidth fullHeight overflow="auto" paddingY={1}>
                <Grid container spacing={2} xs={12}>
                    {/*距離驗證場所最近的國際機場為何*/}
                    <Grid item xs={12}>
                        <TextField name="nearestInternalAirport" label="The closest international airport to the certification place."
                                   variant="outlined" multiline rows={5} fullWidth/>
                    </Grid>
                    {/*從上述國際機場到達驗證場所(農場、工廠)所需時間/方式*/}
                    <Grid item xs={12}>
                        <TextField name="transportationAndTime" label="How do you get from the airport to the certification place, and how long it takes to get there."
                                   variant="outlined" multiline rows={5} fullWidth InputLabelProps={{shrink: true}}/>
                    </Grid>
                </Grid>
            </Box>
            <Box display="flex" justifyContent="space-between" paddingTop={2}>
                <Button text="Back" color="secondary" onClick={props.onBack} />
                <Button text="Next" variant="contained" color="primary" onClick={() => props.onNext()} />
            </Box>
        </Form>
    )
}

// 實驗室資訊
function LabInfo(props){
    const { value, title } = props;

    const validateData = {laboratories: value?.laboratories || []};

    return (
        <Form value={value} onChange={props.onChange} display="flex" flexDirection="column" fullWidth fullHeight overflow="hidden">
            <Box fullWidth gap={1} paddingTop={1}>
                <Typography variant="h5">{i18n.translate(title)}</Typography>
                <Typography color="textSecondary">
                    {i18n.translate("Please provide local pesticide residue test laboratory, and fill in the information of the laboratory below.")}
                </Typography>
            </Box>
            <LaboratoryInfoTable name="laboratories" type="array"/>
            <Box display="flex" justifyContent="space-between" paddingTop={2}>
                <Button text="Back" color="secondary" onClick={props.onBack} />
                <Button text="Next" variant="contained" color="primary" onClick={() => props.onNext(validateData)}/>
            </Box>
        </Form>
    )
}

// 工廠資訊
function FactoryInfo(props){
    const { value, title } = props;
    const validateData = {factories: value?.factories || []};

    return (
        <Form value={value} onChange={props.onChange} fullWidth fullHeight display="flex" flexDirection="column" overflow="hidden">
            <Box paddingTop={1} fullWidth>
                <Typography variant="h5">{i18n.translate(title)}</Typography>
            </Box>
            <UnitInfoTable name="factories" type="array"/>
            <Box display="flex" justifyContent="space-between" paddingTop={2}>
                <Button text="Back" color="secondary" onClick={props.onBack} />
                { value?.applyCrop?
                    <Button text="Next" variant="contained" color="primary" onClick={() => props.onNext(validateData)}/>:
                    <Button text="Complete" variant="contained" color="primary" onClick={() => props.onComplete(validateData, value)}/>
                }
            </Box>
        </Form>
    )
}

// 農場資訊
function FarmInfo(props){
    const { value, title } = props;
    const validateData = {
        identityType: value?.identityType || null,
        groupNumber: value?.groupNumber || null,
        farms: value?.farms || []
    }

    const handleChange = ({value: newValue}) => {
        if(!newValue?.identityType || newValue?.identityType === "individual"){
            newValue.groupNumber = null;
        }
        props.onChange(new UserEvent({value: newValue}));
    }

    return (
        <Form value={value} onChange={handleChange} display="flex" flexDirection="column" fullWidth fullHeight overflow="hidden">
            <Box display="flex" paddingTop={1} paddingBottom={1}>
                <Typography variant="h5">{i18n.translate(title)}</Typography>
            </Box>
            <Box display="flex" flexDirection="column" fullWidth fullHeight overflow="hidden">
                <Box display="flex" justifyContent="space-evenly" width="50%" gap={2} paddingTop={1}>
                    {/*集團或個人*/}
                    <Box width="50%">
                        <SelectField name="identityType" label="Group or Individual" variant="outlined" fullWidth code="identityType" required/>
                    </Box>
                    {/*集團成員數量*/}
                    <Box width="50%">
                        { value.identityType === "group" &&
                            <NumberField name="groupNumber" label="Number of farmers within the farm group" variant="outlined" fullWidth/>
                        }
                    </Box>
                </Box>
                <FarmInfoTable name="farms" type="array"/>
            </Box>
            <Box display="flex" justifyContent="space-between" paddingTop={2}>
                <Button text="Back" color="secondary" onClick={props.onBack} />
                <Button text="Complete" variant="contained" color="primary" onClick={() => props.onComplete(validateData, value)}/>
            </Box>
        </Form>
    )
}

function Certificates(props){
    const { value } = props;
    const [open, setOpen] = useState(false);

    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    const handleDelete = row => () => {
        const newData = [...value];
        newData.splice(row, 1);
        props.onChange(new UserEvent({value: newData}));
    }

    const handleChange = event => {
        const newCertificate = event.value;
        const newData = [...value];
        newData.push(newCertificate);
        props.onChange(new UserEvent({value: newData}));
        setOpen(false);
    }

    const handleDownload = (file) => async () => {
        const descriptor = file;

        if(lang.isNullOrUndefined(descriptor)) return;

        await downloader.save(descriptor);
    }

    const renderFileNameAccessor = (data) => lang.isNullOrUndefined(data.document)? "" : data.document.name;

    const renderDownloadAccessor = (data) => {
        const file = data.document;
        return <Button icon="cloud_download" color="default" variant="icon" onClick={handleDownload(file)}/>
    }

    const renderDeleteAccessor = ({row}) => {
        return <Button icon="delete" color="secondary" variant="icon" onClick={handleDelete(row)} />
    }

    return (
        <Box display="flex" flexDirection="column" fullWidth>
            <Box display="flex" justifyContent="flex-start" alignItems="center">
                <Typography variant={"body1"} color="textSecondary">
                    {i18n.translate("Please attach the valid certificate(s) of NOP, EU, and other organic standards.")}
                </Typography>
                <Button icon="cloud_upload" color="primary" variant="icon" onClick={handleOpen}/>
            </Box>
            <DataTable data={value} stickyHeader dense>
                {/*檔案名稱*/}
                <DataColumn accessor={renderFileNameAccessor} label="File name"/>
                {/*說明*/}
                <DataColumn accessor="description" label="Description"/>
                {/*下載*/}
                <DataColumn accessor={renderDownloadAccessor} label="Download"/>
                {/*刪除*/}
                <DataColumn accessor={renderDeleteAccessor}/>
            </DataTable>
            <UploadDialog open={open} onChange={handleChange} onClose={handleClose}/>
        </Box>
    )
}

function UploadDialog(props){
    const { open } = props;
    const [data, setData] = useState({});

    const handleConfirm = () => {
        props.onChange(new UserEvent({value: data}));
        setData({});
    };

    const handleChange = event => setData(event.value);

    return (
        <StyledDialog open={open}>
            <DialogTitle onClose={props.onClose}>
                {i18n.translate("Upload")}
            </DialogTitle>
            <DialogContent dividers>
                <Form value={data} onChange={handleChange}>
                    <Grid container spacing={2}>
                        {/*選擇檔案*/}
                        <Grid item xs={12}>
                            <FileField name="document" label="Choose file" variant="outlined" fullWidth required/>
                        </Grid>
                        {/*說明*/}
                        <Grid item xs={12}>
                            <TextField name="description" label="Description" variant="outlined" fullWidth multiline/>
                        </Grid>
                    </Grid>
                </Form>
            </DialogContent>
            <DialogActions>
                <Button variant="contained" text="OK" color="primary" onClick={handleConfirm}/>
                <Button variant="contained" text="Cancel" color="primary" onClick={props.onClose}/>
            </DialogActions>
        </StyledDialog>
    )
}

function LaboratoryInfoTable(props){
    const {value} = props;
    const [selection, setSelection] = useState([]);
    const [laboratoryInfos, setLaboratoryInfos] = useState([]);
    const prompt = usePrompt();

    useEffect(()=>{
        setLaboratoryInfos([...value]);
    },[props.value])


    const handleCreate = () => {
        const laboratoryInfo = {}
        const newLaboratoryInfo = [...laboratoryInfos];
        newLaboratoryInfo.push(laboratoryInfo);
        const row = newLaboratoryInfo.length - 1;
        const newSelection = [row];
        props.onChange(new UserEvent({value: newLaboratoryInfo}));
        setLaboratoryInfos(newLaboratoryInfo);
        setSelection(newSelection);
    }

    const handleDelete = () => {
        if(selection.length!==1){
            return prompt.warn("Please select an item to delete");
        }
        const row = lang.first(selection)
        const newLaboratoryInfo = [...laboratoryInfos];
        newLaboratoryInfo.splice(row, 1);

        props.onChange(new UserEvent({value: newLaboratoryInfo}));
        setLaboratoryInfos(newLaboratoryInfo)
    }

    const handleSelect = ({selection}) => {
        if(selection.length!==1) return;
        setSelection(selection);
    }

    const handleChange = (event) => {
        const data = event.value;
        const newLaboratoryInfos = [...laboratoryInfos];
        const row = selection[0];
        newLaboratoryInfos[row] = data;

        props.onChange(new UserEvent({value: newLaboratoryInfos}));
        setLaboratoryInfos(newLaboratoryInfos)
    }

    const renderLaboratoryNameAccessor = (data) => {
        const {name="", testingMethodAndNumbersOfPesticide=""} = data;
        const title = `${i18n.translate("Testing method and numbers of pesticide of the laboratory")}: 
                        ${testingMethodAndNumbersOfPesticide}`;
        return (
            <Tooltip title={title} arrow={true}>
                <Typography>{name}</Typography>
            </Tooltip>
        )

    }

    let data;
    if(selection.length>0) {
        const row = selection[0];
        data = laboratoryInfos[row];
    }

    return (
        <Box display="flex" flexDirection="column" fullWidth fullHeight overflow="auto">
            <Box display="flex" justifyContent="flex-end" fullWidth>
                <Button icon="add_circle" text="Create" color="primary" variant="icon" onClick={handleCreate} />
                <Button icon="delete" text="Delete" color="secondary" variant="icon" onClick={handleDelete} />
            </Box>
            <Box fullWidth>
                <DataTable data={laboratoryInfos} selection={selection} onSelect={handleSelect} stickyHeader numbering dense>
                    {/*實驗室名稱*/}
                    <DataColumn accessor={renderLaboratoryNameAccessor} label="Laboratory name" align="left"/>
                    {/*聯絡人*/}
                    <DataColumn accessor="contactPerson" label="Contact Person" align="center"/>
                    {/*電子郵件*/}
                    <DataColumn accessor="email" label="Email"/>
                    {/*聯絡電話*/}
                    <DataColumn accessor="contactPhone" label="Contact Phone" align="center"/>
                </DataTable>
            </Box>
            { data && <LaboratoryEditor value={data} onChange={handleChange}/> }
        </Box>
    )
}

function LaboratoryEditor(props){
    const {value={}} = props;

    return(
        <Form value={value} onChange={props.onChange} paddingTop={2} fullWidth fullHeight>
            <Grid container spacing={2} style={{width: '100%', margin: 0}}>
                {/*實驗室名稱*/}
                <Grid item xs={3}>
                    <TextField name="name" label="Laboratory name" variant="outlined" fullWidth/>
                </Grid>
                {/*聯絡人*/}
                <Grid item xs={3}>
                    <TextField name="contactPerson" label="Contact person" variant="outlined"  fullWidth/>
                </Grid>
                {/*電子郵件*/}
                <Grid item xs={3}>
                    <TextField name="email" label="Email" variant="outlined" fullWidth/>
                </Grid>
                {/*聯絡電話*/}
                <Grid item xs={3}>
                    <TextField name="contactPhone" label="Contact phone" variant="outlined" fullWidth/>
                </Grid>
                {/*通訊地址*/}
                <Grid item xs={12}>
                    <TextField name="mailingAddress" label="Mailing address" variant="outlined" fullWidth/>
                </Grid>
                {/*實驗室檢測方法及農藥檢測數目*/}
                <Grid item xs={12}>
                    <TextField name="testingMethodAndNumbersOfPesticide" label="Testing method and numbers of pesticide of the laboratory"
                               variant="outlined" multiline fullWidth helperText="The testing methods must be already accredited according to ISO/IEC 17025..."
                               InputLabelProps={{shrink: true}} />
                </Grid>
            </Grid>
        </Form>
    )
}

function UnitInfoTable(props){
    const {value} = props;
    const [selection, setSelection] = useState([]);
    const [unitInfos, setUnitInfos] = useState([]);
    const prompt = usePrompt();

    useEffect(() => {
        setUnitInfos([...value]);
    },[props.value])

    const handleCreate = () => {
        const unitInfo = {};
        const newUnitInfo = [...unitInfos];
        newUnitInfo.push(unitInfo);
        const row = newUnitInfo.length - 1;
        const newSelection = [row];

        props.onChange(new UserEvent({value: newUnitInfo}));
        setSelection(newSelection);
    }

    const handleDelete = () => {
        if(selection.length!==1){
            return prompt.warn("Please select an item to delete");
        }
        const row = lang.first(selection);
        const newUnitInfo = [...unitInfos];
        newUnitInfo.splice(row, 1);

        props.onChange(new UserEvent({value: newUnitInfo}));
        setUnitInfos(newUnitInfo);
    }

    const handleSelect = ({selection}) => {
        if(selection.length!==1) return;
        setSelection(selection);
    }

    const handleChange = (event) => {
        const data = event.value;
        const newUnitInfo = [...unitInfos];
        const row = selection[0];
        newUnitInfo[row] = data;

        props.onChange(new UserEvent({value: newUnitInfo}));
        setUnitInfos(newUnitInfo);
    }

    const renderCoordinateAccessor = (data) => {
        const {latitude="", longitude=""} = data
        return `${lang.isNullOrUndefined(latitude)? "" : latitude} ${lang.isNullOrUndefined(longitude)? "" : longitude}`
    }

    let data;
    if(selection.length>0){
        const row = selection[0];
        data = unitInfos[row];
    }

    return (
        <Box fullWidth fullHeight display="flex" flexDirection="column" overflow="auto">
            <Box display="flex" justifyContent="flex-end">
                <Button icon="add_circle" text="Create" color="primary" variant="icon" onClick={handleCreate} />
                <Button icon="delete" text="Delete" color="secondary" variant="icon" onClick={handleDelete} />
            </Box>
            <Box fullWidth>
                <DataTable data={unitInfos} selection={selection} onSelect={handleSelect} stickyHeader dense numbering>
                    {/*工廠名稱*/}
                    <DataColumn accessor="name" label="Unit name"/>
                    {/*工廠座標*/}
                    <DataColumn accessor={renderCoordinateAccessor} label="Unit Coordinate"/>
                    {/*產品名稱*/}
                    <DataColumn accessor="product" label="Product name"/>
                </DataTable>
            </Box>
            { data && <UnitEditor value={data} onChange={handleChange}/> }
        </Box>
    )
}

function UnitEditor(props){
    const classes = useStyles();
    const {value={}} = props;
    const prompt = usePrompt();

    const handleDownload = async () => {
        try {
            await registrationService.downloadQFP024OrganicProductFile();
            prompt.success('Download the data successfully.');
        } catch (e) {
            prompt.alert(e, {title: 'Failed to download.'});
        }
    }

    return(
        <Form value={value} onChange={props.onChange} fullWidth fullHeight paddingTop={2}>
            <Grid container spacing={2} style={{width: '100%', margin: 0}}>
                {/*工廠名稱*/}
                <Grid item xs={4}>
                    <TextField name="name" label="Unit name" variant="outlined" required fullWidth/>
                </Grid>
                {/*工廠座標-緯度*/}
                <Grid item xs={4}>
                    <TextField name="latitude" label="Unit Latitude" variant="outlined" required fullWidth/>
                </Grid>
                {/*工廠座標-經度*/}
                <Grid item xs={4}>
                    <TextField name="longitude" label="Unit Longitude" variant="outlined" required fullWidth/>
                </Grid>
                {/*工廠地址*/}
                <Grid item xs={12}>
                    <TextField name="address" label="Unit address" variant="outlined" required fullWidth/>
                </Grid>
                {/*產品名稱*/}
                <Grid item xs={8}>
                    <TextField name="product" label="Product name" variant="outlined" required fullWidth/>
                </Grid>
                {/*上傳QF-P024*/}
                <Grid item xs={8}>
                    <FileField name="organicProductProfile" label="Upload QF-P024" variant="outlined" fullWidth/>
                    <Box display="flex" alignItems="center">
                        <Icon color="primary" className={classes.downloadText} onClick={handleDownload}>
                            download
                        </Icon>
                        <Typography variant="body2" onClick={handleDownload} className={classes.downloadText}>
                            {i18n.translate("Download the QF-P024(1.0) Organic Product Profiles &Processing aid form")}
                        </Typography>
                    </Box>
                    <Typography variant="caption" color="textSecondary">{i18n.translate("Please fill the QF-P024(1.0)Organic Product Profiles &Processing aid form if products has multiple ingredients")}</Typography>
                </Grid>
            </Grid>
        </Form>
    )
}

function FarmInfoTable(props){
    const {value} = props;
    const [selection, setSelection] = useState([]);
    const [farmInfos, setFarmInfos] = useState([]);
    const prompt = usePrompt();

    useEffect(() => {
        setFarmInfos([...value]);
    }, [props.value])

    const handleCreate = () => {
        const farmInfo = {};
        const newFarmInfo = [...farmInfos];
        newFarmInfo.push(farmInfo);
        const row = newFarmInfo.length - 1;
        const newSelection = [row];

        props.onChange(new UserEvent({value: newFarmInfo}));

        setSelection(newSelection);
    }

    const handleDelete = () => {
        if(selection.length!==1){
            return prompt.warn("Please select an item to delete");
        }
        const row = lang.first(selection);
        const newFarmInfo = [...farmInfos];
        newFarmInfo.splice(row, 1);

        props.onChange(new UserEvent({value: newFarmInfo}));
        setFarmInfos(newFarmInfo);
    }

    const handleSelect = ({selection}) => {
        if(selection.length!==1) return;
        setSelection(selection);
    }

    const handleChange = (event) => {
        const data = event.value;
        const newFarmInfo = [...farmInfos];
        const row = selection[0];
        newFarmInfo[row] = data;

        props.onChange(new UserEvent({value: newFarmInfo}));
        setFarmInfos(newFarmInfo);
    }

    const renderCoordinateAccessor = (data) => {
        return `${lang.isNullOrUndefined(data?.farmLatitude)? "" : data.farmLatitude} ${lang.isNullOrUndefined(data?.farmLongitude)? "" : data.farmLongitude}`;
    }

    let data;
    if(selection.length>0){
        const row = selection[0];
        data = farmInfos[row];
    }

    return (
        <Box display="flex" flexDirection="column" fullWidth fullHeight overflow="auto">
            <Box display="flex" justifyContent="flex-end">
                <Button icon="add_circle" text="Create" color="primary" variant="icon" onClick={handleCreate} />
                <Button icon="delete" text="Delete" color="secondary" variant="icon" onClick={handleDelete} />
            </Box>
            <Box fullWidth>
                <DataTable data={farmInfos} selection={selection} onSelect={handleSelect} stickyHeader dense numbering>
                    {/*農場/基地名稱*/}
                    <DataColumn accessor="name" label="Farm/Unit name" align="left"/>
                    {/*基地座標*/}
                    <DataColumn accessor={renderCoordinateAccessor} label="Unit Coordinate"/>
                    {/*基地面積(ha)*/}
                    <DataColumn accessor="acreage" label="Acreage(ha)" align="center"/>
                    {/*作物生產期間*/}
                    <DataColumn accessor="harvestPeriod" label="Harvest Period" align="center"/>
                    {/*作物名稱*/}
                    <DataColumn accessor="cropName" label="Crop Name" align="center"/>
                </DataTable>
            </Box>
            { data && <FarmEditor value={data} onChange={handleChange}/> }
        </Box>
    )
}

function FarmEditor(props){
    const {value={}} = props;

    return(
        <Form value={value} onChange={props.onChange} fullWidth fullHeight paddingTop={2}>
            <Grid container spacing={2} style={{width: '100%', margin: 0}}>
                {/*農場/基地名稱*/}
                <Grid item xs={4}>
                    <TextField name="name" label="Farm/Unit name" variant="outlined" required fullWidth/>
                </Grid>
                {/*作物名稱*/}
                <Grid item xs={4}>
                    <TextField name="cropName" label="Crop Name" variant="outlined" required fullWidth/>
                </Grid>
                {/*作物生產期間*/}
                <Grid item xs={4}>
                    <TextField name="harvestPeriod" label="Harvest Period" variant="outlined" required fullWidth/>
                </Grid>
                {/*基地座標-緯度*/}
                <Grid item xs={4}>
                    <TextField name="farmLatitude" label="Farm Latitude" variant="outlined" required fullWidth/>
                </Grid>
                {/*基地座標-經度*/}
                <Grid item xs={4}>
                    <TextField name="farmLongitude" label="Farm Longitude" variant="outlined" required fullWidth/>
                </Grid>
                {/*基地面積*/}
                <Grid item xs={4}>
                    <NumberField name="acreage" label="Acreage(ha)" variant="outlined" step={0.0001} required fullWidth/>
                </Grid>
                {/*場區地址*/}
                <Grid item xs={12}>
                    <TextField name="address" label="Farm address" variant="outlined" required fullWidth
                               InputLabelProps={{shrink: true}} />
                </Grid>
            </Grid>
        </Form>
    )
}

export function OverseasStepper(props){
    const {activeStep} = props;
    const step = activeStep - 1;
    const value = useSelector(selectRegistrationForm);
    const { applyCrop=false, applyProcessing=false } = value;
    const steps = applyCrop && applyProcessing? STEPS.both : (applyCrop && !applyProcessing? STEPS.onlyCrop : (!applyCrop && applyProcessing? STEPS.onlyProcessing : STEPS.initial));

    return(
        <Stepper activeStep={step} orientation="vertical">
            { steps.map((label, index) =>(
                <Step key={label}>
                    <StepLabel><Typography>{i18n.translate(label)}</Typography></StepLabel>
                </Step>
            ))}
        </Stepper>
    )
}

export default OverseasRegistration;